import { useEffect, FunctionComponent, memo, useState } from 'react';
import { Case as CaseType } from '~types';
import { useTranslation } from 'react-i18next';
import { useSubscription, gql } from '@apollo/client';
import _ from 'lodash';
import loadable from '@loadable/component';
import { useConfig, useGetGaTestId } from '~components/hooks';
import { Price } from '~ui/index';
import Currency from '~components/Currency';
import { Loader } from '~components/Loader';
import DisableCase from '~components/home/cases/DisableCase';
import './mysteryCaseStyles.scss';
import {
  Slider,
  PlayButton,
  WinItem,
  FreeCase,
  FreeCaseButton,
  LiveDrops,
  EventBanner,
  ItemsView,
  FreeCaseV2Timer,
  WinItems,
} from '~components/play';
import { Helmet } from 'react-helmet';
import { useGetCase } from '~components/home/hooks';
import {
  useStatusGame,
  useGameCount,
  useCalceOfPossibleFreeOpenPrice,
} from '~components/play/hooks';
import { useProfile } from '~components/profile/hooks';
import { convertNumber } from '~ui/Price';

const WinItemsModal = loadable(
  () => import('~components/modals/WinItemsModal'),
) as any;
import NotFound from '../error/NotFound';

import './casePage.scss';
import '../container.scss';
import './casePreview.scss';
import './casePreviewMultiple.scss';
import '../../scss/pages/_case.scss';
import { useHttpClient } from '~shared/frontend/providers/HttpProvider';
import { RecommendedCases } from '~frontend/components/RecommendedCases';
import dayjs from 'dayjs';
import { useMount } from 'react-use';
import { useYaMetrica } from '~frontend/provider/yametrica';
import CaseHeading from '~frontend/components/play/case-heading/CaseHeading';
import CubeIcon from './cubeIcon';
import RefreshIcon from './refreshIcon';
import RandomIcon from './randomIcon';
import StarIcon from './starIcon';
import FreeCaseNew from '~frontend/components/play/case-opened-free/FreeCaseNew';

interface PlaySubscriptionData {
  updateCase: CaseType;
}

interface PlaySubscriptionVars {
  caseId: number;
}

interface IPlay {
  getCase: CaseType;
}

const Play: FunctionComponent<IPlay> = memo(({ getCase }) => {
  let [countOpen] = useGameCount();
  const getConfig = useConfig();
  const [gameStatus, setStatus] = useStatusGame();
  const getProfile = useProfile();
  let [_ga_ab] = useGetGaTestId();
  const [isFastEndBtnClicked, setFastEndBtnClicked] = useState(false);
  const yametrica = useYaMetrica();

  const handleFastEndBtnClicked = () => {
    setFastEndBtnClicked(true);
  };

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [price] = useCalceOfPossibleFreeOpenPrice(
    getCase,
    getCase?.userOpenForFree,
    countOpen,
  );

  if (getConfig.isEU) _ga_ab = 1;

  useSubscription<PlaySubscriptionData, PlaySubscriptionVars>(
    gql`
      subscription updateCase($caseId: Int!) {
        updateCase(id: $caseId) {
          id
          getLeft
          getLimit
          getDateEnd
          needToOpenForFree
          userOpenForFree
          userFreeOpen
          price
          priceOld
          freeCaseV2minDeposit
          freeCaseV2countFreeOpen
        }
      }
    `,
    { variables: { caseId: getCase?.id } },
  );

  // useMount(() => {
  //   yametrica.sendApi(1, 'event', 'experiment_impression', {
  //     experiment_id: 'show_drop_chance',
  //     variant_id: `show_drop_chance.${_ga_ab}`,
  //     send_to: 'G-C5XHV4XCZ9',
  //   });
  // });

  useEffect(() => {
    setStatus(0);

    if (getCase) {
      window?.dataLayer.push({ ecommerce: null });

      window?.dataLayer.push({
        event: 'view_item',
        ecommerce: {
          currency: getConfig?.getCurrency?.char,
          items: [
            {
              item_name: getCase?.getName,
              item_id: String(getCase?.id),
              quantity: 1,
              price: convertNumber(getCase?.price),
              item_category: String(getCase?.category),
              currency: getConfig?.getCurrency?.char,
            },
          ],
        },
      });

      // window?.fbq('track', 'ViewContent', {
      //   content_name: getCase?.getName,
      //   content_type: 'product',
      //   value: convertNumber(getCase?.price),
      //   currency: getConfig?.getCurrency?.char,
      //   content_ids: [getCase?.id],
      // });
    }

    return () => setStatus(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCase?.id, setStatus]);

  const title =
    _.get(getCase, `seo[${language}].title`) ||
    t('Case_Title', { caseName: getCase?.getName });
  const description =
    _.get(getCase, `seo[${language}].description`) ||
    t('Case_Desc', { caseName: getCase?.getName });
  const keywords =
    _.get(getCase, `seo[${language}].keywords`) || getCase?.seo?.keywords;

  const schema = {
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: getCase?.getName,
      description: getCase?.seo?.description,
      image: getCase?.getImage,
      sku: getCase?.id,
      brand: {
        '@type': 'Brand',
        name: 'CSCASE',
      },
      review: {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        author: {
          '@type': 'Person',
          name: `${t('User_few')} CSCASE`,
        },
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5',
        ratingCount: _.toInteger(getCase?.id * 2.5),
      },
      offers: {
        '@type': 'AggregateOffer',
        offerCount: '100',
        lowPrice: convertNumber(getCase?.price),
        highPrice: convertNumber(getCase?.price * 10),
        priceCurrency: getConfig?.getCurrency?.char,
      },
    }),
  };

  const isMobile =
    getConfig?.userAgent.match(
      /Android|BlackBerry|iPhone|iPod|Opera Mini|IEMobile|WPDesktop/i,
    ) ?? false;

  if (isMobile) countOpen = 1;

  return (
    <>
      <Helmet script={[schema]}>
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords} />}
      </Helmet>
      <WinItemsModal />
      <EventBanner />
      <div className="case1">
        <div className="container">
          <CaseHeading getCase={getCase} />
          {/* <div
            className={`case_previews`}
            //data-rar={price === 0 ? '06c50c' : getCase?.color}
          > */}
          {[0, 1].includes(gameStatus) && (
            <div
              className={`case_previews`}
              //data-rar={price === 0 ? '06c50c' : getCase?.color}
            >
              {getCase?.getActiveGame && getCase?.type === 3 ? (
                <FreeCaseNew getCase={getCase} />
              ) : !gameStatus && price > getProfile?.money ? (
                <>
                  {_.map(_.times(countOpen), (key) => (
                    <div
                      className={`${
                        countOpen > 1 && countOpen % 2 !== 0
                          ? 'case_preview_item_multiple'
                          : ''
                      } ${
                        (countOpen === 3 && key === 1) ||
                        (countOpen === 5 && key === 2)
                          ? 'large_item_multiple'
                          : ''
                      }`}
                      data-game-count={countOpen}
                      key={key}
                    >
                      {' '}
                      <img
                        //key={key}
                        src={getCase?.getBackgroundImage}
                        alt={getCase?.getName}
                        width={424}
                        height={424}
                        className={`${
                          countOpen > 1 && countOpen % 2 !== 0
                            ? 'case_preview_image_multiple'
                            : 'case_preview_image'
                        }`}
                      />
                    </div>
                  ))}
                  {/* <div className="case-warning case-warning-dont-money">
                    <span className="icon material-icons">campaign</span>
                    <div className="case-warning-title">
                      {t('Just a little to open the case')}!
                    </div>
                    {
                      <div className="case-warning-text">
                        {t('By replenishing the account at least')}{' '}
                        {getConfig.isEU ? (
                          <>
                            <Currency />
                            <Price sum={price - getProfile?.money} />
                          </>
                        ) : (
                          <>
                            <Price sum={price - getProfile?.money} />
                            <Currency />
                          </>
                        )}{' '}
                        {t(
                          'or by selling items for this amount, you can immediately open it',
                        )}
                        !
                      </div>
                    }
                  </div> */}
                </>
              ) : (
                <>
                  {_.map(_.times(countOpen), (key) => (
                    <div
                      className={`${
                        countOpen > 1 && countOpen % 2 !== 0
                          ? 'case_preview_item_multiple'
                          : ''
                      } ${
                        (countOpen === 3 && key === 1) ||
                        (countOpen === 5 && key === 2)
                          ? 'large_item_multiple'
                          : ''
                      }`}
                      data-game-count={countOpen}
                      key={key}
                    >
                      {' '}
                      <img
                        //key={key}
                        src={getCase?.getImage}
                        alt={getCase?.getName}
                        width={424}
                        height={424}
                        className={`${
                          countOpen > 1 && countOpen % 2 !== 0
                            ? 'case_preview_image_multiple'
                            : 'case_preview_image'
                        }`}
                      />
                    </div>
                  ))}

                  {/* {getCase?.type === 7 && (
                    <div className={`case-warning-dont-money case-warning`}>
                      <span className="icon material-icons">campaign</span>

                      <div className="case-warning-text">
                        {getCase?.freeCaseV2minDeposit <=
                          getProfile?.deposit24Hour &&
                        getProfile?.deposit24HourTtl > 0 ? (
                          <>
                            {t('PLAY.NOTY.FREE_CASE_V_2_IS_OPEN')}{' '}
                            <FreeCaseV2Timer
                              deposit24HourTtl={dayjs()
                                .add(
                                  getProfile?.deposit24HourTtl,
                                  'milliseconds',
                                )
                                .format()}
                            />
                          </>
                        ) : (
                          t('PLAY.NOTY.FREE_CASE_V_2', {
                            countFreeOpen: getCase?.freeCaseV2countFreeOpen,
                            minDeposit: convertNumber(
                              getCase?.freeCaseV2minDeposit,
                            ),
                            currency: getConfig?.getCurrency.char,
                            deposit24Hour: convertNumber(
                              getProfile.deposit24Hour,
                            ),
                          })
                        )}
                      </div>
                    </div>
                  )} */}

                  {/* {getCase?.type === 5 && (
                    <div className={`case-warning-dont-money case-warning`}>
                      <span className="icon material-icons">campaign</span>

                      <div className="case-warning-text">
                        {t(
                          'In order to unlock this case, you need to recharge your account with promo code',
                        )}
                        <br />
                        <div className="case-warning-title">
                          {_.get(_.first(getCase?.getPromoCodes), 'items')}
                        </div>
                      </div>
                    </div>
                  )} */}

                  {/* {(getCase?.type === 4 || getCase?.type === 3) && (
                    <div className={`case-warning-dont-money case-warning`}>
                      <span className="icon material-icons">campaign</span>
                      {getCase?.type === 3 && getProfile ? null : (
                        <div className="case-warning-title">
                          {t('Try it now')}!
                        </div>
                      )}
                      <div className="case-warning-text">
                        {getCase?.type === 3 && !getProfile ? (
                          <>
                            {t('You can open this case')}{' '}
                            {t('as soon as you enter the site')}! <br />{' '}
                          </>
                        ) : (
                          <>
                            {t('Free and Farm cases')} {t('cheap skins')}{' '}
                            {t('sell automatically')}
                          </>
                        )}
                      </div>
                    </div>
                  )} */}
                </>
              )}
            </div>
          )}
          {/* </div> */}

          <Slider isFastEndBtnClicked={isFastEndBtnClicked} />

          <WinItem getCase={getCase} />
          <WinItems getCase={getCase} />

          {getCase?.getActiveGame && getCase?.type === 3 ? (
            <FreeCaseButton getCase={getCase} />
          ) : (
            <PlayButton
              getCase={getCase}
              onFastEndBtnClick={handleFastEndBtnClicked}
            />
          )}

          {getCase?.type === 6 ? (
            <div className="case_mystery">
              <div className="case_mystery_header">
                <CubeIcon />
                <h2 className="case_mystery_title">ТАИНСТВЕННЫЕ КЕЙСЫ</h2>
              </div>

              <div className="case_mystery_features">
                <div className="case_mystery_feature">
                  <RefreshIcon />
                  <p>Каждый день новый лут в кейсах</p>
                </div>

                <div className="case_mystery_feature">
                  <RandomIcon />
                  <p>Полный рандом – призы остаются загадкой</p>
                </div>

                <div className="case_mystery_feature">
                  <StarIcon />
                  <p>Испытай удачу и получи крутой скин!</p>
                </div>
              </div>

              <p className="case_mystery_description">
                Mystery Case — это кейс с полностью случайным наполнением, в
                котором могут выпасть скины разных качеств, перчатки, ножи, а
                также самые элитные предметы из нашего ассортимента.
              </p>
            </div>
          ) : (
            // <div className="case-mistery-warning">
            //   <div className="in-case">
            //     <div className="in-case-title title-fair-play">
            //       <span className="name">{t('Case Contains')}</span>
            //     </div>
            //   </div>
            //   <div className="battle-pass-quest-list-desclaimer">
            //     <div className="icon material-icons" data-icon="campaign"></div>
            //     <div className="battle-pass-quest-list-desclaimer-text">
            //       <p>{t('This case is unstable')}</p>
            //       <p>{t('His drops change every day')}</p>
            //     </div>
            //   </div>
            // </div>
            <>
              {/* {_ga_ab === 1 && <LiveDrops />} */}
              <ItemsView getCase={getCase} />
              <RecommendedCases />
            </>
          )}
        </div>
      </div>
      {/*/////////////////////////////// */}
      {/* <DisableCase
        getCase={getCase}
        classNames={`case-play ${gameStatus && 'case-open'}`}
      > */}
      {/* <h1
          className="case-play-name"
          data-rar={price === 0 ? '06c50c' : getCase?.color}
        >
          {getCase?.getName}
        </h1> */}
      {/* <div
          className={`case-item ${
            getCase?.getActiveGame && getCase?.type === 3 && 'case-free'
          }`}
          data-rar={price === 0 ? '06c50c' : getCase?.color}
        > */}
      {/* {[0, 1].includes(gameStatus) && (
            <section>
              <div className="case-item-img" data-game-count={countOpen}>
                {getCase?.getActiveGame && getCase?.type === 3 ? (
                  <FreeCase getCase={getCase} />
                ) : !gameStatus && price > getProfile?.money ? (
                  <>
                    {_.map(_.times(countOpen), (key) => (
                      <img
                        key={key}
                        src={getCase?.getImage}
                        alt={getCase?.getName}
                      />
                    ))}
                    <div className="case-warning case-warning-dont-money">
                      <span className="icon material-icons">campaign</span>
                      <div className="case-warning-title">
                        {t('Just a little to open the case')}!
                      </div>
                      {
                        <div className="case-warning-text">
                          {t('By replenishing the account at least')}{' '}
                          {getConfig.isEU ? (
                            <>
                              <Currency />
                              <Price sum={price - getProfile?.money} />
                            </>
                          ) : (
                            <>
                              <Price sum={price - getProfile?.money} />
                              <Currency />
                            </>
                          )}{' '}
                          {t(
                            'or by selling items for this amount, you can immediately open it',
                          )}
                          !
                        </div>
                      }
                    </div>
                  </>
                ) : (
                  <>
                    {_.map(_.times(countOpen), (key) => (
                      <img
                        key={key}
                        src={getCase?.getImage}
                        alt={getCase?.getName}
                      />
                    ))}

                    {getCase?.type === 7 && (
                      <div className={`case-warning-dont-money case-warning`}>
                        <span className="icon material-icons">campaign</span>

                        <div className="case-warning-text">
                          {getCase?.freeCaseV2minDeposit <=
                            getProfile?.deposit24Hour &&
                          getProfile?.deposit24HourTtl > 0 ? (
                            <>
                              {t('PLAY.NOTY.FREE_CASE_V_2_IS_OPEN')}{' '}
                              <FreeCaseV2Timer
                                deposit24HourTtl={dayjs()
                                  .add(
                                    getProfile?.deposit24HourTtl,
                                    'milliseconds',
                                  )
                                  .format()}
                              />
                            </>
                          ) : (
                            t('PLAY.NOTY.FREE_CASE_V_2', {
                              countFreeOpen: getCase?.freeCaseV2countFreeOpen,
                              minDeposit: convertNumber(
                                getCase?.freeCaseV2minDeposit,
                              ),
                              currency: getConfig?.getCurrency.char,
                              deposit24Hour: convertNumber(
                                getProfile.deposit24Hour,
                              ),
                            })
                          )}
                        </div>
                      </div>
                    )}

                    {getCase?.type === 5 && (
                      <div className={`case-warning-dont-money case-warning`}>
                        <span className="icon material-icons">campaign</span>

                        <div className="case-warning-text">
                          {t(
                            'In order to unlock this case, you need to recharge your account with promo code',
                          )}
                          <br />
                          <div className="case-warning-title">
                            {_.get(_.first(getCase?.getPromoCodes), 'items')}
                          </div>
                        </div>
                      </div>
                    )}

                    {(getCase?.type === 4 || getCase?.type === 3) && (
                      <div className={`case-warning-dont-money case-warning`}>
                        <span className="icon material-icons">campaign</span>
                        {getCase?.type === 3 && getProfile ? null : (
                          <div className="case-warning-title">
                            {t('Try it now')}!
                          </div>
                        )}
                        <div className="case-warning-text">
                          {getCase?.type === 3 && !getProfile ? (
                            <>
                              {t('You can open this case')}{' '}
                              {t('as soon as you enter the site')}! <br />{' '}
                            </>
                          ) : (
                            <>
                              {t('Free and Farm cases')} {t('cheap skins')}{' '}
                              {t('sell automatically')}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
          )}

          <Slider />

          <WinItem getCase={getCase} />
          <WinItems getCase={getCase} /> */}

      {/* {getCase?.getActiveGame && getCase?.type === 3 ? (
            <>
              <div className="case-free-descr">
                <div className="text">
                  {t('Great prize! Get it')},{' '}
                  <span className="text-yellow">
                    {getCase?.seo?.inputMoney} <Currency isText={true} />{' '}
                    {t('to the account')}
                  </span>
                  <br /> {t('and')}{' '}
                  <span className="text-yellow">
                    30 {t('free_case_opening_descrip')}
                  </span>{' '}
                  {t('this free case')}{' '}
                  <span className="text-yellow">{t('for a present')}!</span>
                </div>
              </div>
              <FreeCaseButton getCase={getCase} />
            </>
          ) : (
            <PlayButton getCase={getCase} />
          )} */}
      {/* </div> */}
      {/* </DisableCase> */}
    </>
  );
});

const PlayWrap = () => {
  const { setHttpContext } = useHttpClient();
  const { loading, error, data } = useGetCase({});

  const getCase = data?.getCase;

  if (loading) {
    return <Loader />;
  } else if (error) {
    return <>Play | Error! {error.message}</>;
  }

  if (!getCase) {
    setHttpContext({ redirect: '/' });
    return <NotFound />;
  }

  return <Play getCase={getCase} />;
};

export default PlayWrap;
